import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import '../index.css';

const Calculator = () => {
    const [formData, setFormData] = useState({
        location: 'Boulder, CO',
        practice: 'reduce tillage',
        acres: 600,
        carbonSequestrationRate: 0.5, // tons of CO2 per acre per year
        carbonCreditPrice: 20, // $ per ton of CO2
    });
    const [graphData, setGraphData] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Improved calculation logic
        const { acres, carbonSequestrationRate, carbonCreditPrice } = formData;
        const yearlyEarnings = Array.from({ length: 5 }, (_, i) => 
            acres * carbonSequestrationRate * carbonCreditPrice * (1 + i * 0.05) // assuming a 5% increase in carbon credit price per year
        );

        setGraphData({
            labels: ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5'],
            datasets: [{
                label: 'Estimated Earnings ($)',
                data: yearlyEarnings,
                backgroundColor: 'rgb(92, 212, 150)',
                borderColor: 'rgba(76, 175, 80, 1)',
                borderWidth: 2,
            }],
        });
    };

    const chartOptions = {
        plugins: {
            legend: {
                labels: {
                    color: '#333',
                    font: {
                        family: "'SF Pro Display', sans-serif",
                        size: 14
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#333',
                bodyColor: '#333',
                borderColor: 'rgba(76, 175, 80, 0.7)',
                borderWidth: 1,
                cornerRadius: 12,
                displayColors: false,
                padding: 12,
                titleFont: {
                    family: "'SF Pro Display', sans-serif",
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    family: "'SF Pro Display', sans-serif",
                    size: 14
                },
                callbacks: {
                    label: function(context) {
                        return `$ ${context.parsed.y.toFixed(2)}`;
                    }
                }
            }
        },
        scales: {
            x: {
                ticks: { 
                    color: '#333',
                    font: {
                        family: "'SF Pro Display', sans-serif",
                        size: 12
                    }
                },
                grid: { 
                    color: 'rgba(200, 200, 200, 0.3)',
                    drawBorder: false
                }
            },
            y: {
                ticks: { 
                    color: '#333',
                    font: {
                        family: "'SF Pro Display', sans-serif",
                        size: 12
                    },
                    callback: function(value) {
                        return '$ ' + value;
                    }
                },
                grid: { 
                    color: 'rgba(200, 200, 200, 0.3)',
                    drawBorder: false
                }
            }
        },
        maintainAspectRatio: false,
        responsive: true,
        animation: {
            duration: 1000,
            easing: 'easeOutQuart'
        }
    };

    return (
        <div className="min-h-screen p-8 bg-white text-gray-800 flex flex-col items-center justify-center w-full font-sans">
            <div className="mb-6 text-center">
                <h2 className="font-bold text-3xl text-gray-800 mb-2">SphereSix</h2>
                <h3 className="text-xl text-green-600 font-medium">Carbon Calculator</h3>
            </div>
            
            <div className="flex flex-col lg:flex-row gap-8 w-full max-w-6xl">
                {/* Form Section */}
                <div className="bg-white p-6 rounded-2xl shadow-lg w-full lg:w-2/5 border border-gray-100">
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">Location</label>
                            <div className="relative">
                                <select 
                                    name="location" 
                                    value={formData.location} 
                                    onChange={handleChange} 
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
                                >
                                    <option>Boulder, CO</option>
                                    <option>Denver, CO</option>
                                    <option>Fort Collins, CO</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">Practice</label>
                            <div className="relative">
                                <select 
                                    name="practice" 
                                    value={formData.practice} 
                                    onChange={handleChange} 
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent pr-10"
                                >
                                    <option>Reduce tillage</option>
                                    <option>Plant cover crops</option>
                                    <option>Improve soil health</option>
                                </select>
                                <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">Acres</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    name="acres" 
                                    value={formData.acres} 
                                    onChange={handleChange} 
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-gray-400 text-sm">acres</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">Carbon Sequestration Rate</label>
                            <div className="relative">
                                <input 
                                    type="number" 
                                    name="carbonSequestrationRate" 
                                    value={formData.carbonSequestrationRate} 
                                    onChange={handleChange} 
                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-gray-400 text-sm">tons/acre/year</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block mb-2 text-sm font-medium text-gray-700">Carbon Credit Price</label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <span className="text-gray-400">$</span>
                                </div>
                                <input 
                                    type="number" 
                                    name="carbonCreditPrice" 
                                    value={formData.carbonCreditPrice} 
                                    onChange={handleChange} 
                                    className="w-full p-3 pl-8 bg-gray-50 border border-gray-200 rounded-xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-gray-400 text-sm">per ton</span>
                                </div>
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            className="w-full bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 hover:bg-green-600 hover:text-teal-100 text-white font-medium p-3 rounded-xl mt-4 shadow-md transition-colors duration-200 flex items-center justify-center space-x-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                            </svg>
                            <span>Calculate Earnings</span>
                        </button>
                    </form>
                </div>
                
                {/* Graph Section */}
                <div className="bg-white p-6 rounded-2xl shadow-lg w-full lg:w-3/5 border border-gray-100 flex flex-col">
                    <h3 className="font-medium text-gray-700 mb-4 text-center">Projected Carbon Credit Earnings</h3>
                    <div className="flex-grow h-64">
                        {graphData ? (
                            <Bar data={graphData} options={chartOptions} />
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center">
                                <div className="w-16 h-16 mb-4 rounded-full bg-green-100 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                                    </svg>
                                </div>
                                <p className="text-gray-500">Fill out the form and click "Calculate Earnings"<br/>to see your 5-year projection</p>
                            </div>
                        )}
                    </div>
                    {graphData && (
                        <div className="mt-4 bg-green-50 p-4 rounded-xl">
                            <p className="text-sm text-gray-700">
                                Based on your inputs, we estimate total earnings of 
                                <span className="font-bold text-green-700 mx-1">
                                    ${graphData.datasets[0].data.reduce((a, b) => a + b, 0).toFixed(2)}
                                </span>
                                over 5 years.
                            </p>
                        </div>
                    )}
                </div>
            </div>
            
            <p className="text-gray-500 text-xs mt-6">
                ©2025 SphereSix - Empowering farmers to make a difference
            </p>
        </div>
    );
};

export default Calculator;